import cn from 'classnames';
import React, {useCallback} from 'react';
import {Button, ButtonProps} from '../Button';
import {Icon, IconName} from '../Icon';

import styles from './IconButton.module.css';

export type IconButtonSize = 'sm' | 'md' | 'xl' | 'mobile';

type IconButtonClasses = {
	root: string;
	iconBox: string;
	icon: string;
	circularIconBox: string;
	squareIconBox: string;
	circularIconBoxTransparent: string;
	contentBox: string;
};

export type IconButtonProps = ButtonProps & {
	iconName?: IconName;
	tooltip?: string;
	icon?: React.ReactElement;
	iconPosition?: 'left' | 'right';
	classes?: Partial<IconButtonClasses>;
	loading?: boolean;
	circularIcon?: boolean;
	squareIcon?: boolean;
	onIconClick?: (event: React.MouseEvent) => void;
	size?: IconButtonSize;
	transparentIconBg?: boolean;
	mobileButton?: boolean;
};

export function IconButton({
	icon,
	iconName,
	iconPosition = 'left',
	className,
	classes = {},
	circularIcon,
	squareIcon,
	loading,
	tooltip,
	children,
	onIconClick,
	size = 'md',
	transparentIconBg,
	mobileButton,
	...buttonProps
}: IconButtonProps) {
	const hasIcon = Boolean(icon || iconName || loading);
	const hasContent = Boolean(children);

	const handleIconClick = useCallback(
		(event: React.MouseEvent) => {
			if (onIconClick) {
				event.stopPropagation();
				onIconClick(event);
			}
		},
		[onIconClick],
	);

	const iconBox = hasIcon && (
		<div
			onClick={handleIconClick}
			className={cn(styles.iconBox, classes.iconBox, {
				[styles.circularIconBox]: circularIcon,
				[styles.squareIconBox]: squareIcon,
				[styles.circularIconBoxTransparent]: transparentIconBg,
			})}
		>
			{loading && <Icon name="loading" className={classes.icon} />}
			{!loading && icon}
			{!loading && !icon && iconName && <Icon name={iconName} className={classes.icon} />}
		</div>
	);

	return (
		<Button
			className={cn('group', styles.root, classes.root || className, {
				'w-6 h-6': size === 'sm',
				'min-w-max': size === 'mobile',
				[styles.hasIcon]: hasIcon,
				[styles.hasContent]: hasContent,
				[styles[iconPosition]]: true,
				[styles.mobileButton]: mobileButton,
			})}
			{...buttonProps}
		>
			{tooltip && (
				<div className={styles.tooltipWrapper}>
					<div className={styles.tooltip}>{tooltip}</div>
				</div>
			)}
			{iconPosition === 'left' && iconBox}
			{hasContent && <div className={cn(styles.contentBox, classes.contentBox)}>{children}</div>}
			{iconPosition === 'right' && iconBox}
		</Button>
	);
}
